import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import { useInView } from '../lib/useInView';

const cards = [
  {
    quote: 'EXPLAIN THE CODE',
    title: 'You shipped it. You cannot explain it.',
    body: 'Copilot wrote it, you accepted it, tests passed. A reviewer asks why — and your brain blanks.',
  },
  {
    quote: 'DEBUG & INTERVIEW',
    title: 'You paste fixes. You cannot trace them live.',
    body: 'Something breaks → paste into AI → apply the patch. On a screen share you cannot isolate the same bug alone.',
  },
  {
    quote: 'NO COMPASS',
    title: 'You feel productive. You are not sure you grew.',
    body: 'Tutorials finish. Your portfolio fills up. In the interview you freeze on the same question again.',
  },
];

const beforeItems = [
  'Copy-paste the AI answer',
  'Ship without understanding',
  'Freeze when asked "why?"',
];

const afterItems = [
  'Attempt first, then hints',
  'Explain your reasoning out loud',
  'See where you still lean on AI',
];

const ProblemTransform = () => {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.35, once: true });
  const [phase, setPhase] = useState<'idle' | 'before' | 'after'>('idle');

  useEffect(() => {
    if (!inView) return;
    setPhase('before');
    const id = window.setTimeout(() => setPhase('after'), 1600);
    return () => window.clearTimeout(id);
  }, [inView]);

  return (
    <div
      ref={ref}
      className={[
        'problem__transform',
        inView ? 'problem__transform--in' : '',
        phase === 'after' ? 'problem__transform--after' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Before and after: from AI copy-paste to independent practice"
    >
      <article className="problem__panel problem__panel--before tile">
        <p className="problem__panel-label">Before · AI only</p>
        <h3 className="problem__panel-title">Fast output. No ownership.</h3>
        <ul className="problem__panel-list">
          {beforeItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </article>

      <div className="problem__arrow" aria-hidden="true">
        <span className="problem__arrow-icon">→</span>
        <span className="problem__arrow-label">Prepzio</span>
      </div>

      <article className="problem__panel problem__panel--after tile">
        <p className="problem__panel-label">After · With Prepzio</p>
        <h3 className="problem__panel-title">Same speed. Real skill.</h3>
        <ul className="problem__panel-list">
          {afterItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </article>
    </div>
  );
};

export const Problem = () => {
  const { ref, inView } = useInView<HTMLElement>({ threshold: 0.12, once: true });

  return (
    <section
      ref={ref}
      className={`section problem${inView ? ' problem--revealed' : ''}`}
      id="problem"
      aria-labelledby="problem-title"
    >
      <div className="container">
        <p className="eyebrow">Sound familiar?</p>
        <h2 id="problem-title" className="h-section h-section--wide">
          AI made you faster. Did it make you better?
        </h2>
        <p className="lede">
          Most juniors use AI every day. A smaller group still cannot walk through a basic loop
          without help. Both can be true at once.
        </p>

        <ProblemTransform />

        <div className="problem__grid">
          {cards.map((c, i) => (
            <article
              key={c.title}
              className="tile problem__card"
              style={{ '--card-i': i } as CSSProperties}
            >
              <p className="problem__quote">{c.quote}</p>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
